import { MECHANICS } from '../../../config/mechanics';
import { renderMasterySystemMatrix, setMatrixSort, bindMasterySystemMatrix } from './MasterySystemMatrix';
import { getConfig } from './types';

type MechCategory = 'tag' | 'system' | 'stat' | 'math';

interface MechEntry {
  id: string;
  category: MechCategory;
  name: string;
  description: string;
  /** Optional formula code block (rendered as monospace). */
  code?: string;
  /** Optional follow-up text after the code block. */
  followText?: string;
  /** Optional example block (italic-leaning, accent border). */
  example?: string;
}

const FORMULAS: MechEntry[] = [
  {
    id: 'momentum_turn_order',
    category: 'math',
    name: 'Momentum & Turn Order',
    description: 'Every combatant has a momentum counter that starts at 0. Each tick, every alive unit gains momentum equal to their effective Speed. When a unit reaches 10,000 momentum, they take a turn.',
    code: 'ticks_to_act = ceil((10000 - current_momentum) / effective_speed)',
    followText: 'After acting, the unit\'s momentum is reduced by 10,000. Ties: players act before enemies, then by slot index.',
    example: 'A unit with 8 Speed needs ceil(10000/8) = 1250 ticks to act. A unit with 12 Speed needs only 834 ticks. It acts ~50% more often.',
  },
  {
    id: 'player_damage_pipeline',
    category: 'math',
    name: 'Player Damage Pipeline',
    description: 'Player damage is resolved per-element component. Each added damage source (skill base, weapon added, infusion added) is calculated independently against the target\'s resistance to that element. Then three damage pools each apply as their own multiplicative step: increased (additive bucket), attacker more (Empower / Fury / Vanish / Rallied / First Strike), and target vulnerability (Marked / Fractured / Exposed / Broken / weakness multipliers).',
    code: `baseDmg = skillBase + (weaponFlat + infusionFlat) * effectiveness
increased% = masteryRank + gearIncreasedDmg + tagMods + str_up/int_up
           + conditional (barrier active, vs Wounded, vs Debuffed, vs Full Health)
afterIncrease = baseDmg * (1 + increased% / 100)
effectiveRes = chartBase + selfElRes + templateMod + gearRes - attackerPen
resMult = clamp(1 - effectiveRes / 100, 0.25, 1.75)
afterRes = afterIncrease * resMult
attackerMore% = Empower + Fury + Vanish + Rallied + First Strike
afterAttackerMore = afterRes * (1 + attackerMore% / 100)
targetVuln% = Marked + Fractured + Exposed + Broken + weakness mults + ...
afterTargetVuln = afterAttackerMore * (1 + targetVuln% / 100)
finalDmg = max(1, floor(totalAllComponents * critMult))`,
    example: 'A fire skill rolls 20-30. Weapon adds 5-8 fire flat at 0.8 effectiveness = 4-6.4. With 15% increased from mastery rank 15 and a 30% Obscuration debuffed bonus: (27 + 5) * 1.45 = 46.4. Against -50 fire res (nature target): 46.4 * 1.5 = 69.6. With Empower active (+20% more): 69.6 * 1.20 = 83.5. Against a Marked target (+25%): 83.5 * 1.25 = 104.4 final.',
  },
  {
    id: 'enemy_damage_pipeline',
    category: 'math',
    name: 'Enemy Damage Pipeline',
    description: 'Enemy abilities declare an explicit damage range, scaled by enemy level and the encounter ATK multiplier, then run through resistance.',
    code: `rolled   = roll(minDamage..maxDamage) * levelScale * encounterAtkMult
afterRes = rolled * resMult
finalDmg = max(1, floor(afterRes))`,
  },
  {
    id: 'critical_hits',
    category: 'math',
    name: 'Critical Hits',
    description: 'Crit chance is rolled once per attack. The multiplier applies to the total damage (all element components combined) after resistance.',
    code: `baseCrit = abilityCrit + flatGearCrit
critChance = baseCrit * (1 + increasedGearCrit / 100)
critMultiplier = 1.5 + gearCritMulti / 100`,
    example: 'Ability with 5% base crit, +10% flat from a Keen Edge sword, +50% increased from "of Acuity" suffix: (5 + 10) * 1.5 = 22.5% crit chance.',
  },
  {
    id: 'resistance_penetration',
    category: 'math',
    name: 'Resistance & Penetration',
    description: 'Resistance is clamped: minimum 0.25x damage (75% resist cap) and maximum 1.75x damage (75% vulnerability cap). Penetration directly subtracts from the target\'s total resistance before the multiplier is calculated.',
    code: `effectiveRes = chartBase + selfElementRes(+50 magical only)
          + templateResistance + gearResistance
          - attackerPenetration
damageMult = clamp(1 - effectiveRes / 100, 0.25, 1.75)`,
    example: 'Fire vs Nature: chartBase = -50 (nature is weak to fire). Nature\'s self-element res = +50 (vs nature only). If target has +20 fire gear res and attacker has 15 fire pen: effectiveRes = -50 + 20 - 15 = -45. Mult = 1.45x.',
  },
  {
    id: 'mastery_scaling',
    category: 'math',
    name: 'Mastery Scaling',
    description: 'Each mastery rank grants its mastery\'s unique per-rank bonus (Spellcraft: +1% increased Spell Damage, Bladecraft: +1 Added Damage, Obscuration: +2% increased Damage against Debuffed enemies, etc.) — the "Per Rank" line on each mastery card. Cost per rank is flat. Each strand has per-mastery cost multipliers across 7 tiers (0.5× Prodigy to 4.0× Hostile), which determines how cheap or expensive each mastery is for that strand.',
    code: `rankCost = round(multiplier * 4)  (flat per rank)
actualCost = rankCost * strandCostMultiplier
rankBonus = perRank * masteryRank  (per the mastery's rankBonus line)`,
    example: 'Rank 10 Spellcraft on a 1.0× cost strand: each rank costs 4 points (40 total for R10), grants +10% increased Spell Damage. On a 2.0× cost strand the same R10 costs 80 points.',
  },
  {
    id: 'dot_math',
    category: 'math',
    name: 'DoT (Damage over Time)',
    description: 'DoT effects tick at the start of the afflicted unit\'s turn. They use the same resistance formula as direct damage. Same-type DoTs refresh duration and take the higher damage value (no stacking).',
    code: `tickDmg = max(1, floor(dotDamage * resMult))
// applied at start of afflicted unit's turn
// stacks refresh duration, take higher damage`,
  },
  {
    id: 'stagger_math',
    category: 'math',
    name: 'Stagger (Momentum Penalty)',
    description: 'Stagger reduces the target\'s momentum, delaying their next turn. Repeated stagger applications on the same target have diminishing returns. Each subsequent hit halves the penalty (minimum 375).',
    code: `penalty = max(375, floor(basePenalty / 2^stackCount))`,
  },
  {
    id: 'guard_math',
    category: 'math',
    name: 'Guard (Damage Reduction)',
    description: 'Guard reduces incoming damage using a diminishing-returns formula. It is strong against many small hits but weak against single large hits. The signature of heavy armor. Applied after resistance and before Barrier absorption.',
    code: `dr% = guard / (guard + 12 × rawDamage), max 90%
finalDmg = rawDamage × (1 − dr%)`,
    example: '200 Guard vs a 50-damage hit: dr% = 200 / (200 + 600) = 25%, final = 37. Against a 10-damage hit: 62.5% reduction, final = 4. Against a 300-damage hit: 5.3% reduction, final = 284.',
  },
  {
    id: 'damage_pools',
    category: 'math',
    name: 'The Three Damage Pools',
    description: 'Damage flows through three independent pools after the base damage is rolled and resistance is applied. Each pool is a sum-additive bucket internally, then the bucket multiplies the damage line as a single step. Pools multiply against each other — so a 1.5× increased pool times a 1.2× attacker-more pool times a 1.3× target-vuln pool is 2.34× total. This is why "more" multipliers (which have their own pool) feel stronger than "increased" multipliers (which dilute against gear).',
    code: `Pool 1 — Increased (additive, gear-pooled):
  gear affixes + tagIncreased masteries + str_up/int_up
  + barrier_active_damage + damage_per_fervor_stack
  + bonus_vs_full_hp / wounded / debuffed (conditional)

Pool 2 — Attacker More (multiplicative, attacker-side):
  Empower + Fury + Vanish + Rallied + First Strike

Pool 3 — Target Vulnerability (multiplicative, target-side):
  Marked + Fractured + Exposed + Broken
  + weakness multipliers + Condemn + tempoConsumeBonus
  + debuffCountBonus + per-party-effect riders + Rot`,
    followText: 'Tooltips use "increased" for Pool 1 sources and Pool 3 target-amplifier debuffs (PoE Shock-style convention — the keyword Marked/Fractured/etc. signals the bucket). Tooltips use "more" only for Pool 2 attacker-side buffs. This wording rule is the player\'s cue for which bucket a modifier lives in.',
    example: '15 mastery ranks (Pool 1) + 8% gear + 50% STR Up + 30% vs Debuffed = 103% in Pool 1 (×2.03). Empower active (Pool 2) = +20% (×1.20). Target is Marked (Pool 3) = +25% (×1.25). Total multiplier on base = 2.03 × 1.20 × 1.25 = 3.05× before crit and resistance.',
  },
];

const CAT_LABELS: Record<MechCategory | 'all', string> = {
  all: 'All',
  tag: 'Ability Tags',
  system: 'Systems',
  stat: 'Key Stats',
  math: 'Combat Math',
};

interface MechanicsState {
  categoryFilter: MechCategory | '';
}

// Persistent in-module state — chapter is stateless from the caller's view, so we
// hold filter state here. The chapter is recreated only when content innerHTML changes.
const state: MechanicsState = { categoryFilter: '' };

export function renderMechanics(content: HTMLElement): void {
  const all: MechEntry[] = [
    ...MECHANICS.map(m => ({
      id: m.id,
      category: m.category as MechCategory,
      name: m.name,
      description: m.description,
    })),
    ...FORMULAS,
  ];

  const counts: Record<string, number> = { all: all.length };
  for (const c of ['tag', 'system', 'stat', 'math'] as MechCategory[]) {
    counts[c] = all.filter(e => e.category === c).length;
  }

  const filtered = state.categoryFilter
    ? all.filter(e => e.category === state.categoryFilter)
    : all;

  const pillRow = (['all', 'tag', 'system', 'stat', 'math'] as const).map(c =>
    `<button class="codex-pill${(state.categoryFilter === '' && c === 'all') || state.categoryFilter === c ? ' active' : ''}" data-mech-cat="${c}">${CAT_LABELS[c]} <span class="codex-pill-count">${counts[c]}</span></button>`
  ).join('');

  const cardsHtml = filtered.map(e => {
    const codeHtml = e.code ? `<pre class="codex-formula-code">${escapeHtml(e.code)}</pre>` : '';
    const followHtml = e.followText ? `<div class="codex-mechanic-follow">${e.followText}</div>` : '';
    const exampleHtml = e.example ? `<div class="codex-formula-example">${e.example}</div>` : '';

    return `
      <div class="codex-mechanic-card-v2">
        <div class="codex-mechanic-cat-badge codex-mechanic-cat-badge--${e.category}">${CAT_LABELS[e.category]}</div>
        <div class="codex-mechanic-name">${e.name}</div>
        <div class="codex-mechanic-desc">${e.description}</div>
        ${codeHtml}
        ${followHtml}
        ${exampleHtml}
      </div>
    `;
  }).join('');

  const config = getConfig();
  const matrixHtml = config ? renderMasterySystemMatrix(config) : '';

  content.innerHTML = `
    <h2 class="codex-section-title">Mechanics</h2>
    <p class="codex-section-subtitle">Keywords, systems, and the math that ties them together.</p>
    ${matrixHtml}
    <div class="codex-filter-pills">${pillRow}</div>
    <div class="codex-mechanic-list">${cardsHtml}</div>
  `;

  content.querySelectorAll('[data-mech-cat]').forEach(btn => {
    btn.addEventListener('click', () => {
      const c = (btn as HTMLElement).dataset.mechCat!;
      state.categoryFilter = (c === 'all') ? '' : (c as MechCategory);
      renderMechanics(content);
    });
  });

  content.querySelectorAll('[data-matrix-sort]').forEach(th => {
    (th as HTMLElement).style.cursor = 'pointer';
    th.addEventListener('click', () => {
      const key = (th as HTMLElement).dataset.matrixSort!;
      setMatrixSort(key);
      renderMechanics(content);
    });
  });

  if (config) bindMasterySystemMatrix(content, config);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
